import React, { useState } from 'react';
import {
  Button,
  ButtonColor,
  ButtonSize,
  ButtonVariant,
  CheckboxInput,
  FilterContext,
  IconType,
  ListDataRefresher,
  ListWithPagination,
  RelativeRow,
  useListContext,
} from 'ui-builder';
import { Button as ShadcnButton } from 'shadcn-ui';
import { PlusIcon } from 'lucide-react';
import useFeatureToggleService from 'feature-toggle';
import {
  CommentPermission, PROJECT_NOTES_UPDATED_EVENT,
  ProjectNote,
  ProjectNoteRequest, ProjectPermission,
  useProjectNotesService
} from 'nekst-api';
import CommentsList from '../../shared/widgets/CommentsList/CommentsList';
import SidebarBlock from '../../shared/web/layout/SidebarBlock';
import CommentRow from '../../shared/widgets/CommentsList/CommentRow';
import Right from '../../shared/web/Right';
import AddCommentForm from '../../shared/widgets/CommentsList/AddCommentForm';
import ShadcnNoteForm from '../../shared/widgets/CommentsList/ShadcnNoteForm';
import OverflowHidden from '../../shared/widgets/OverflowHidden';
import Left from '../../shared/web/Left';
import IsAddedToClientPortalField from './IsAddedToClientPortalField';
import { useAuthorizationChecker } from 'authorization-scope';
import { PaginationWrapper, RowRepeater } from 'features/nekst-widgets';
import { ProjectIdContext, TextWithLinksField, usePopupFormManager } from 'features/nekst-widgets';

interface Props {
  projectId: number;
}

export function ProjectNoteAdditionalInputs() {
  const { isGranted } = useAuthorizationChecker();

  if (isGranted(ProjectPermission.ASSIGN_NOTE_TO_CLIENT_PORTAL)) {
    return (
      <Left>
        <CheckboxInput
          source="isAddedToClientPortal"
          label="Add this note to the Client Portal"
        />
      </Left>
    );
  } else {
    return null;
  }
}

function AddNoteButton(props: Props) {
  const projectNotesService = useProjectNotesService();
  const popupFormManager = usePopupFormManager();
  const { isGranted } = useAuthorizationChecker();

  const openCreatePopup = () => {
    popupFormManager.openForm(
      <ShadcnNoteForm<ProjectNote, ProjectNoteRequest>
        title="Add Note"
        submitFunc={async (data) => {
          return await projectNotesService.createNote(props.projectId, data) as ProjectNote;
        }}
        additionalInputs={<ProjectNoteAdditionalInputs />}
      />,
      true,
    );
  };

  if (isGranted(ProjectPermission.CREATE_NOTE)) {
    return (
      <ShadcnButton
        variant="outline"
        size="icon-sm"
        onClick={openCreatePopup}
        aria-label="Add note"
      >
        <PlusIcon className="size-4" />
      </ShadcnButton>
    );
  } else {
    return null;
  }
}

function LegacyTitle(props: Props) {
  const [showForm, setShowForm] = useState<boolean>();

  const projectNotesService = useProjectNotesService();

  const { isGranted } = useAuthorizationChecker();

  return (
    <>
      <OverflowHidden>
        <Right>
          {isGranted(ProjectPermission.CREATE_NOTE) && (
            <Button
              text="Add"
              size={ButtonSize.SMALL}
              color={ButtonColor.BLUE}
              variant={ButtonVariant.FILLED}
              onClick={() => {
                setShowForm((prev) => !prev);
              }}
              iconType={IconType.PLUS}
            />
          )}
        </Right>
        <h3>Notes</h3>
      </OverflowHidden>
      {showForm && (
        <AddCommentForm
          submitFunc={async (data: ProjectNoteRequest) => {
            return await projectNotesService.createNote(props.projectId, data) as ProjectNote;
          }}
          afterSubmitFunc={async () => {
            setShowForm(false);
          }}
          closeFormFunc={() => setShowForm(false)}
          additionalInputs={<ProjectNoteAdditionalInputs />}
        />
      )}
    </>
  );
}

function ShadcnViewFields() {
  return (
    <>
      <TextWithLinksField source="_renderedText" />
      <IsAddedToClientPortalField />
    </>
  );
}

function LegacyViewFields() {
  return (
    <RelativeRow weights={[10, 2]}>
      <TextWithLinksField source="_renderedText" />
      <IsAddedToClientPortalField />
    </RelativeRow>
  );
}

function NotesSidebarBlock(props: Props) {
  const { total } = useListContext();
  const projectNotesService = useProjectNotesService();

  return (
    <SidebarBlock
      title="Notes"
      count={total}
      headerRight={<AddNoteButton projectId={props.projectId} />}
      testId="project-notes"
    >
      <PaginationWrapper>
        <RowRepeater noDataMessage="There are no notes related to the transaction">
          <CommentRow
            deleteItemFunc={projectNotesService.deleteNote}
            updateItemFunc={async (id: number, request: ProjectNoteRequest) => {
              return await projectNotesService.updateNote(id, request) as ProjectNote;
            }}
            deletePermission={CommentPermission.DELETE}
            updatePermission={CommentPermission.UPDATE}
            additionalInputs={<ProjectNoteAdditionalInputs />}
            viewFields={<ShadcnViewFields />}
          />
        </RowRepeater>
      </PaginationWrapper>
    </SidebarBlock>
  );
}

function ShadcnProjectNotesList(props: Props) {
  const projectNotesService = useProjectNotesService();

  return (
    <FilterContext>
      <ListWithPagination<ProjectNote, null>
        getDataFunc={async (_, limit) => projectNotesService.getProjectNotesPaginated(props.projectId, limit)}
        byPage={5}
      >
        <NotesSidebarBlock projectId={props.projectId} />
        <ListDataRefresher eventName={PROJECT_NOTES_UPDATED_EVENT} />
      </ListWithPagination>
    </FilterContext>
  );
}

function LegacyProjectNotesList(props: Props) {
  const projectNotesService = useProjectNotesService();

  return (
    <CommentsList<ProjectNote, ProjectNoteRequest>
      testId="project-notes"
      getDataFunc={
        async () => {
          return await projectNotesService.getProjectNotes(props.projectId) as ProjectNote[];
        }
      }
      title={<LegacyTitle projectId={props.projectId} />}
      updateItemFunc={async (id: number, request: ProjectNoteRequest) => {
        return await projectNotesService.updateNote(id, request) as ProjectNote;
      }}
      deleteItemFunc={projectNotesService.deleteNote}
      deletePermission={CommentPermission.DELETE}
      updatePermission={CommentPermission.UPDATE}
      messages={{
        noDataText: 'There are no notes related to the transaction',
        textInputLabel: 'Enter Note Here',
      }}
      additionalInputs={<ProjectNoteAdditionalInputs />}
      viewFields={<LegacyViewFields />}
    >
      <ListDataRefresher eventName={PROJECT_NOTES_UPDATED_EVENT} />
    </CommentsList>
  );
}

export default function ProjectNotesList(props: Props) {
  const { isShadcnUiEnabled } = useFeatureToggleService();

  return (
    <ProjectIdContext projectId={props.projectId}>
      {isShadcnUiEnabled()
        ? <ShadcnProjectNotesList projectId={props.projectId} />
        : <LegacyProjectNotesList projectId={props.projectId} />}
    </ProjectIdContext>
  );
}
